import React, { useEffect, useState, useRef } from 'react';
import { SKYLINE_BOOKING_URL } from '../utils/bookingNav';

/**
 * BookSlotPromoBubble
 * --------------------------------------------------------------
 * 1. On first visit (per session), shows a mini "Book Your Slot"
 *    popup 2.5s after page load (bottom-right card).
 * 2. When dismissed, hides for the rest of the session and
 *    the persistent sidebar tab below takes over the entry point.
 * 3. Sidebar tab pulses gently after 30s of user inactivity to
 *    re-attract attention.
 *
 * The "ENQUIRE NOW" sidebar tab lives in App.js. This component
 * renders the new "BOOK YOUR SLOT" sidebar tab right below it —
 * positioning is co-ordinated via fixed top% offsets so both tabs
 * stack neatly with equal spacing.
 */

const SESSION_KEY = 'bookslot_popup_dismissed';
const PERSONA_KEY = 'memcoskyline_persona';
const PRIMARY = '#008751';
const PRIMARY_DARK = '#006e42';

const trackPromoEvent = (name, label) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', name, {
        event_category: 'promo',
        event_label: label,
      });
    }
    if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, label });
    }
  } catch (_) { /* analytics non-critical */ }
};

const handleBookSlotClick = (source) => {
  trackPromoEvent('book_slot_cta_click', source);
  if (typeof window !== 'undefined') {
    window.location.href = SKYLINE_BOOKING_URL;
  }
};

const BookSlotPromoBubble = ({ onContactClick }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [closing, setClosing] = useState(false);
  const [pulse, setPulse] = useState(false);
  const idleTimerRef = useRef(null);

  // Show popup on every reload (no session gating), 2.5s after page load
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const t = window.setTimeout(() => {
      setShowPopup(true);
      trackPromoEvent('book_slot_popup_shown', 'auto_2500ms');
    }, 2500);
    return () => window.clearTimeout(t);
  }, []);

  // 2. Idle detection — pulse sidebar tab after 30s of no interaction
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const ACTIVE_EVENTS = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    const resetIdle = () => {
      setPulse(false);
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = window.setTimeout(() => {
        setPulse(true);
        trackPromoEvent('book_slot_sidebar_pulse', 'idle_30s');
      }, 30000);
    };

    resetIdle();
    ACTIVE_EVENTS.forEach((e) => window.addEventListener(e, resetIdle, { passive: true }));
    return () => {
      ACTIVE_EVENTS.forEach((e) => window.removeEventListener(e, resetIdle));
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    };
  }, []);

  const dismissPopup = () => {
    if (closing) return; // prevent double-trigger
    setClosing(true);
    trackPromoEvent('book_slot_popup_dismissed', 'user_close');
    // play the retract-into-tab animation, then unmount
    window.setTimeout(() => {
      setShowPopup(false);
      setClosing(false);
    }, 320);
  };

  return (
    <>
      {/* ───────────── Mini Popup (springs out from the right-edge tab) ───────────── */}
      {showPopup && (
        <>
          {/* Backdrop — fades to match popup phase */}
          <div
            className={`fixed inset-0 z-[9999] bg-black/40 backdrop-blur-[2px] ${closing ? 'animate-bsp-fade-out' : 'animate-bsp-fade-in'}`}
            onClick={dismissPopup}
            data-testid="book-slot-popup-backdrop"
          />
          <div
            className={`fixed top-1/2 left-1/2 z-[10000] ${closing ? 'animate-bsp-collapse-to-tab' : 'animate-bsp-grow-from-tab'}`}
            style={{
              maxWidth: '480px',
              width: 'calc(100% - 32px)',
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Book your site visit"
            data-testid="book-slot-popup"
          >
          <div className="relative rounded-2xl shadow-2xl overflow-hidden bg-white">
            {/* Close (top-right) */}
            <button
              type="button"
              onClick={dismissPopup}
              aria-label="Close popup"
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl leading-none w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors z-10"
              data-testid="book-slot-popup-close"
            >
              ×
            </button>

            {/* Body */}
            <div className="px-7 sm:px-9 pt-9 pb-8 text-center">
              {/* Calendar icon */}
              <div
                className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
                style={{ background: PRIMARY }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                  <path d="m9 16 2 2 4-4" />
                </svg>
              </div>

              {/* Headline */}
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight mb-4 tracking-tight">
                Reserve your JUTAKU Experience
              </h3>

              {/* Subtitle */}
              <p className="text-sm sm:text-base text-gray-500 leading-relaxed mb-7 max-w-md mx-auto">
                A curated 60-minute walkthrough of MEMCO Skyline&rsquo;s Jutaku Experience Center in Bangalore &mdash; Singasandra. Pick your preferred date &amp; time slot now before they&rsquo;re booked!
              </p>

              {/* Primary CTA */}
              <button
                type="button"
                onClick={() => handleBookSlotClick('popup_cta')}
                className="w-full text-white text-sm sm:text-base font-bold tracking-wider uppercase py-4 rounded-full transition-colors shadow-sm"
                style={{ background: PRIMARY }}
                onMouseEnter={(e) => { e.currentTarget.style.background = PRIMARY_DARK; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = PRIMARY; }}
                data-testid="book-slot-popup-cta"
              >
                Book Your Site Visit
              </button>

              {/* Secondary link — contact */}
              <button
                type="button"
                onClick={() => {
                  dismissPopup();
                  if (typeof onContactClick === 'function') onContactClick('popup_contact');
                  trackPromoEvent('concierge_cta_click', 'popup');
                }}
                className="mt-5 text-sm font-semibold transition-opacity hover:opacity-80"
                style={{ color: PRIMARY }}
                data-testid="book-slot-popup-concierge"
              >
                Contact Us →
              </button>
            </div>
          </div>
        </div>
        </>
      )}

      {/* ───────────── Sidebar tab: BOOK YOUR SITE VISIT (top edge 2px below 40% viewport line) ───────────── */}
      <div
        style={{
          position: 'fixed',
          right: 0,
          top: '40%',
          transform: 'translateY(2px)',
          zIndex: 9997,
        }}
      >
        <button
          type="button"
          onClick={() => handleBookSlotClick('sidebar_tab')}
          className={`text-white py-4 px-2 md:py-8 md:px-3 text-[10px] md:text-xs font-bold tracking-wider transition-colors uppercase whitespace-nowrap rounded-l-xl shadow-lg ${pulse ? 'animate-bsp-pulse' : ''}`}
          style={{
            writingMode: 'vertical-lr',
            background: PRIMARY,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = PRIMARY_DARK; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = PRIMARY; }}
          data-testid="book-slot-side-btn"
        >
          BOOK YOUR SITE VISIT
        </button>
      </div>

      {/* Local keyframes — popup springs out of and folds back into the right-edge tab */}
      <style>{`
        @keyframes bsp-fade-in {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        .animate-bsp-fade-in { animation: bsp-fade-in 0.3s ease-out both; }

        @keyframes bsp-fade-out {
          0%   { opacity: 1; }
          100% { opacity: 0; }
        }
        .animate-bsp-fade-out { animation: bsp-fade-out 0.3s ease-in both; }

        /* Origin point: right edge of viewport, BOOK YOUR SITE VISIT center sits at 40vh + ~95px (tab half-height) → from viewport center that's (40vh + 95px − 50vh) = (95px − 10vh) */
        @keyframes bsp-grow-from-tab {
          0% {
            opacity: 0;
            transform: translate(calc(50vw - 100%), calc(-50% + 95px - 10vh)) scale(0);
          }
          40% { opacity: 1; }
          100% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        .animate-bsp-grow-from-tab { animation: bsp-grow-from-tab 0.42s cubic-bezier(0.34, 1.56, 0.64, 1) both; }

        @keyframes bsp-collapse-to-tab {
          0% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
          60% { opacity: 1; }
          100% {
            opacity: 0;
            transform: translate(calc(50vw - 100%), calc(-50% + 95px - 10vh)) scale(0);
          }
        }
        .animate-bsp-collapse-to-tab { animation: bsp-collapse-to-tab 0.32s cubic-bezier(0.55, 0, 0.85, 0.6) both; }

        @keyframes bsp-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(0, 135, 81, 0.55); }
          70%  { box-shadow: 0 0 0 12px rgba(0, 135, 81, 0);   }
          100% { box-shadow: 0 0 0 0 rgba(0, 135, 81, 0);     }
        }
        .animate-bsp-pulse { animation: bsp-pulse 1.8s ease-out infinite; }
      `}</style>
    </>
  );
};

export default BookSlotPromoBubble;
